"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import RichText from "../RichText";
import { TeamMemberExpanded } from "./TeamGrid.props";
import { X } from "lucide-react";
import { Box } from "@/components/atoms/Box";
import { RawButton } from "@/components/atoms/RawButton";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";

/**
 * Properties for the TeamBioExpansion component.
 * @interface TeamBioExpansionProps
 * @property {TeamMemberExpanded | null} member - The team member whose bio is being expanded.
 * @property {() => void} onClose - Callback function to close the expansion.
 */
interface TeamBioExpansionProps {
  member: TeamMemberExpanded | null;
  onClose: () => void;
}

/**
 * TeamBioExpansion component displays a full-width inline bio for a selected team member.
 * Includes animations for opening and closing, and a dark themed container for premium presentation.
 *
 * @param {TeamBioExpansionProps} props - The properties for the TeamBioExpansion component.
 * @returns {JSX.Element} The rendered TeamBioExpansion component.
 */
const collapsedStyle = {
  gridTemplateRows: '0fr'
} as const;
const expandedStyle = {
  gridTemplateRows: '1fr',
  marginTop: '32px'
} as const;
export const TeamBioExpansion = t0 => {
  const $ = _c(8);
  const {
    member,
    onClose
  } = t0;
  const t1 = member ? expandedStyle : collapsedStyle;
  let t2;
  if ($[0] !== member || $[1] !== onClose) {
    t2 = member && <Box className="section-aq-navy relative rounded-bio p-10 md:p-16 shadow-2xl ring-1 ring-white/10"><RawButton onClick={onClose} className="absolute right-8 top-8 rounded-full p-2 transition-all duration-300 hover:bg-white/10 hover:rotate-90 active:scale-90" aria-label="Close bio"><X size={28} /></RawButton><Box className="max-w-4xl"><Heading level={3} className="mb-4 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl text-white">About <Text className="text-primary">{member.name.split(" ")[0]}</Text></Heading><Text as="p" className="mb-12 text-xl md:text-2xl font-medium text-white/60 tracking-tight">{member.title}</Text><Box className="prose prose-lg md:prose-xl max-w-none opacity-90 leading-relaxed font-light"><RichText data={member.bio} /></Box></Box></Box>;
    $[0] = member;
    $[1] = onClose;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <Box className="overflow-hidden">{t2}</Box>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <Box className="col-span-full grid transition-[grid-template-rows] duration-500 ease-in-out" style={t1}>{t3}</Box>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};